import { useState } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { useAuth } from '../../context/AuthContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { PAGES } from '../../lib/constants'

const NAV_ITEMS = [
  { page: PAGES.DASHBOARD, label: 'Dashboard', icon: DashboardIcon },
  { page: PAGES.INCOME_CAREER, label: 'Income & Career', icon: IncomeCareerIcon },
  { page: PAGES.FINANCIAL_HEALTH, label: 'Financial Health', icon: FinancialHealthIcon },
  { page: PAGES.LOANS_DEBT, label: 'Loans & Debt', icon: LoansDebtIcon },
  { page: PAGES.LIFE_DECISIONS, label: 'Life Decisions', icon: LifeDecisionsIcon },
  { page: PAGES.INVESTING_RETIREMENT, label: 'Investing & Retirement', icon: InvestingRetirementIcon },
]

export default function Sidebar() {
  const { state, navigate, toggleTheme, toggleSidebar } = useFinance()
  const { logout } = useAuth()
  const { isPro } = useSubscription()
  const collapsed = state.sidebarCollapsed
  const [hovered, setHovered] = useState(false)

  // When collapsed, expand on hover
  const expanded = !collapsed || hovered

  return (
    <aside
      onMouseEnter={() => collapsed && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="fixed z-30 max-lg:hidden flex flex-col transition-all duration-300 ease-out overflow-hidden"
      style={{
        left: 12,
        top: 12,
        bottom: 12,
        width: expanded ? 252 : 64,
        background: 'var(--sidebar-bg)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        borderRadius: 24,
        border: '1px solid var(--sidebar-border)',
        boxShadow: `
          0 8px 32px rgba(0, 0, 0, 0.12),
          0 2px 8px rgba(0, 0, 0, 0.08),
          inset 0 1px 0 rgba(255, 255, 255, 0.05)
        `,
      }}
    >
      {/* Ambient glow behind bubble */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: 24,
          background: `radial-gradient(ellipse at 50% 0%, color-mix(in srgb, var(--primary) 6%, transparent), transparent 70%)`,
        }}
      />
      {/* Liquid edge shimmer */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: 24,
          background: `
            linear-gradient(
              180deg,
              color-mix(in srgb, var(--primary) 8%, transparent) 0%,
              transparent 15%,
              transparent 85%,
              color-mix(in srgb, var(--primary) 5%, transparent) 100%
            )
          `,
        }}
      />

      {/* Header */}
      <div className="relative p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
        <button
          onClick={() => navigate(PAGES.DASHBOARD)}
          className="flex items-center gap-3 hover:opacity-80 transition overflow-hidden"
        >
          <img src="/app-icon.png" alt="Liquid Finance" className="w-9 h-9 rounded-xl object-cover shrink-0" />
          <h1
            className="text-base font-bold text-heading whitespace-nowrap transition-all duration-300"
            style={{ opacity: expanded ? 1 : 0, width: expanded ? 'auto' : 0 }}
          >
            Liquid Finance
          </h1>
        </button>
        {expanded && (
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg text-faint hover:text-heading transition shrink-0"
            title={collapsed ? 'Pin sidebar' : 'Collapse sidebar'}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              {collapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="relative flex-1 p-2 space-y-1">
        {NAV_ITEMS.map(({ page, label, icon: Icon }) => {
          const active = state.page === page
          return (
            <button
              key={page}
              onClick={() => navigate(page)}
              title={!expanded ? label : undefined}
              className={`w-full flex items-center gap-3 rounded-2xl text-sm font-medium transition-all duration-200 ${
                active
                  ? 'text-heading'
                  : 'text-muted hover:text-heading'
              }`}
              style={{
                padding: expanded ? '10px 12px' : '10px',
                justifyContent: expanded ? 'flex-start' : 'center',
                background: active ? 'linear-gradient(90deg, color-mix(in srgb, var(--primary) 35%, transparent), transparent)' : undefined,
                borderLeft: active ? '3px solid var(--primary)' : '3px solid transparent',
              }}
            >
              <Icon active={active} />
              {expanded && (
                <span className="whitespace-nowrap">{label}</span>
              )}
            </button>
          )
        })}
      </nav>

      {/* Footer */}
      <div className="relative p-2" style={{ borderTop: '1px solid var(--sidebar-border)' }}>
        {/* Upgrade to Pro */}
        {!isPro && (
          <button
            onClick={() => navigate(PAGES.PRICING)}
            title={!expanded ? 'Upgrade to Pro' : undefined}
            className="w-full flex items-center gap-3 rounded-2xl text-sm font-medium transition-all duration-200 text-primary hover:text-heading mb-1"
            style={{
              padding: expanded ? '10px 12px' : '10px',
              justifyContent: expanded ? 'flex-start' : 'center',
              background: 'var(--primary-soft)',
            }}
          >
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            {expanded && <span className="whitespace-nowrap">Upgrade to Pro</span>}
          </button>
        )}

        {/* Settings */}
        <button
          onClick={() => navigate(PAGES.SETTINGS)}
          title={!expanded ? 'Settings' : undefined}
          className={`w-full flex items-center gap-3 rounded-2xl text-sm font-medium transition-all duration-200 ${
            state.page === PAGES.SETTINGS
              ? 'text-heading'
              : 'text-muted hover:text-heading'
          }`}
          style={{
            padding: expanded ? '10px 12px' : '10px',
            justifyContent: expanded ? 'flex-start' : 'center',
            background: state.page === PAGES.SETTINGS ? 'linear-gradient(90deg, color-mix(in srgb, var(--primary) 35%, transparent), transparent)' : undefined,
            borderLeft: state.page === PAGES.SETTINGS ? '3px solid var(--primary)' : '3px solid transparent',
          }}
        >
          <svg className="w-4 h-4 shrink-0" style={state.page === PAGES.SETTINGS ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {expanded && <span className="whitespace-nowrap">Settings</span>}
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={!expanded ? (state.theme === 'dark' ? 'Light Mode' : 'Dark Mode') : undefined}
          className="w-full flex items-center gap-3 rounded-2xl text-sm text-muted hover:text-heading transition-all"
          style={{
            padding: expanded ? '10px 12px' : '10px',
            justifyContent: expanded ? 'flex-start' : 'center',
          }}
        >
          {state.theme === 'dark' ? (
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
          {expanded && (
            <span className="whitespace-nowrap">{state.theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          )}
        </button>

        {/* Sign Out */}
        <button
          onClick={logout}
          title={!expanded ? 'Sign Out' : undefined}
          className="w-full flex items-center gap-3 rounded-2xl text-sm text-muted hover:text-negative transition-all"
          style={{
            padding: expanded ? '10px 12px' : '10px',
            justifyContent: expanded ? 'flex-start' : 'center',
          }}
        >
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          {expanded && <span className="whitespace-nowrap">Sign Out</span>}
        </button>
      </div>
    </aside>
  )
}

function DashboardIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  )
}

function LoansDebtIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  )
}

function IncomeCareerIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function FinancialHealthIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
    </svg>
  )
}

function LifeDecisionsIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
    </svg>
  )
}

function InvestingRetirementIcon({ active }) {
  return (
    <svg className="w-4 h-4 shrink-0" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  )
}
